import { useEffect, useState } from 'react';
import { FiEdit } from 'react-icons/fi';
import {
  BillingAddressForm,
  SubmitButton
} from '../subscription/subscription-payment/BillingAddressForm';
import { getTranslationKey } from '../../modules/utils';
import styled from 'styled-components';
import { Alert, Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { getPaymentInfo } from '../../modules/selectors/paymentInfo';
import { Title } from './common';
import { isBillingAddressFilled } from '../../common/is-billing-address-filled';
import { DisplayCard, Header } from './DisplayCard';
import { useCountry } from '../../hooks/useCountry';
import { useCountriesList } from '../../hooks/useCountriesList';
import { getUser } from '../../modules/selectors/user';
import media from '../../assets/css/media';

const DACH_COUNTRY_IDS = ['germany', 'austria', 'switzerland'];

const FormWrapper = styled.div`
  width: 50%;
  @media all and (max-width: ${media.mobile}) {
    width: 100%;
  }
`;

const FiEditBtn = styled(FiEdit)`
  cursor: pointer;
  align-self: flex-start;
  min-width: 20px;
  min-height: 20px;
`;

const SubmitBtn: SubmitButton = ({ onClick, disabled }) => (
  <Button
    color="primary"
    className="px-5 mt-3 text-uppercase"
    disabled={disabled}
    type="button"
    onClick={onClick}
  >
    {getTranslationKey('save')}
  </Button>
);

const BillingAddressTabContent = () => {
  const [isSubmit, setIsSubmit] = useState(false);
  const { address: billingAddress } = useSelector(getPaymentInfo);
  const billingAddressIsFilled = isBillingAddressFilled(billingAddress);
  const [showForm, setShowForm] = useState(!billingAddressIsFilled);
  const country = useCountry(billingAddress.country);
  const { taxSettings } = useSelector(getUser);
  useCountriesList();
  const isGermany = country?.name === 'Deutschland';
  const formCountry = useSelector(
    (state: any) => formValueSelector('billindAddressForm')(state, 'country') as string | undefined
  );
  const effectiveCountry = (showForm && formCountry) || billingAddress.country;
  const showDigitalOnlyHint = !!effectiveCountry && !DACH_COUNTRY_IDS.includes(effectiveCountry);

  useEffect(() => {
    if (billingAddressIsFilled) {
      setShowForm(false);
    }
  }, [billingAddressIsFilled]);

  return (
    <div className="new-design">
      <Title className="mb-4">
        {getTranslationKey(
          showForm
            ? 'shop.paymentSettings.changeBillingAddress'
            : 'shop.paymentSettings.yourBillingAddress'
        )}
      </Title>
      <div
        className="mb-5"
        dangerouslySetInnerHTML={{
          __html: getTranslationKey('shop.paymentSettings.billingAddressInfo')
        }}
      />
      {showDigitalOnlyHint && (
        <Alert color="warning" className="mb-4">
          {getTranslationKey('shop.taxConfig.digitalProductsOnlyHint')}
        </Alert>
      )}
      <FormWrapper>
        {showForm ? (
          <BillingAddressForm
            showVatId
            settingsTab
            showTitle={false}
            saveOnBlur={false}
            isSubmit={isSubmit}
            setIsSubmit={setIsSubmit}
            setVatIdHasUnsavedChanges={() => {}}
            setBillingAddressHasUnsavedChanges={() => {}}
            submitButton={SubmitBtn}
            onCancel={() => setShowForm(false)}
          />
        ) : (
          <DisplayCard className="animated fadeIn">
            <Header>
              <div>
                <div>
                  {billingAddress.firstName} {billingAddress.lastName}
                </div>
                <div>{billingAddress.email}</div>
                <div>{billingAddress.company}</div>
                <div>
                  {billingAddress.street} {billingAddress.streetNumber}
                </div>
                <div>
                  {billingAddress.zipcode} {billingAddress.city}
                </div>
                {!!country?.name && <div>{country.name}</div>}
                {!!taxSettings?.vatId && !isGermany && <div>{taxSettings.vatId}</div>}
              </div>
              <FiEditBtn size={20} onClick={() => setShowForm(true)} />
            </Header>
          </DisplayCard>
        )}
      </FormWrapper>
    </div>
  );
};

export const BillingAddressTab = BillingAddressTabContent;
